import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface StickerProps {
  image: string;
  brand: string;
  name: string;
  price: number;
  k: number; // 스티커 가로 px
  height: number; // 스티커 세로 px
  onRemove?: () => void;
  style?: React.CSSProperties;
  // Affiliate tracking props - NOT USED (purchase tracking only)
  productId?: string;
  contentId?: string;
  tagId?: string;
  creatorId?: string;
  brandId?: string;
  isActive?: boolean; // Whether the sticker is approved and within date range
  onClick?: () => void; // Custom click handler
  // Customization props
  showImage?: boolean; // 제품 이미지 표시 여부 (기본: true)
  stickerStyle?: 'default' | 'compact' | 'minimal'; // 스티커 스타일
}

export default function Sticker({
  image,
  brand,
  name,
  price,
  k,
  height,
  onRemove,
  style,
  productId,
  contentId,
  tagId,
  creatorId,
  brandId,
  isActive = true,
  onClick,
  showImage = true, // 기본값: 이미지 표시
  stickerStyle = 'default', // 기본값: 기본 스타일
}: StickerProps) {
  const photoSize = 0.27 * k;
  const nameFont = 0.07 * k;
  const descFont = 0.05 * k;
  const priceFont = 0.07 * k;

  const handleStickerClick = () => {
    // Only call custom onClick handler if provided
    // NO click tracking - only purchase tracking is needed
    if (onClick) {
      onClick();
    }
  };

  return (
    <Box sx={{ position: 'relative', width: k, height }}>
      {/* 삭제 버튼 (스티커 바깥쪽 우측 상단, 빨간색) */}
      {onRemove && (
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            position: 'absolute',
            right: -height * 0.19,
            top: -height * 0.19,
            zIndex: 2,
            bgcolor: '#d32f2f',
            boxShadow: 1,
            width: height * 0.38,
            height: height * 0.38,
            color: '#d32f2f',
            border: '2px solid #d32f2f',
          }}
        >
          <CloseIcon sx={{ fontSize: height * 0.32, color: '#fff' }} />
        </IconButton>
      )}
      {/* 스티커 본체 */}
      <Box
        onClick={handleStickerClick}
        sx={{
          position: 'absolute',
          width: k,
          height,
          bgcolor: isActive ? '#fff' : '#f5f5f5',
          borderRadius: k * 0.02,
          boxShadow: isActive ? '0 4px 24px rgba(0,0,0,0.10)' : '0 2px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          px: k * 0.01,
          py: height * 0.05,
          overflow: 'hidden',
          opacity: isActive ? 1 : 0.6,
          cursor: isActive ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s ease',
          '&:hover': isActive
            ? {
                transform: 'scale(1.02)',
                boxShadow: '0 6px 28px rgba(0,0,0,0.15)',
              }
            : {},
          ...style,
        }}
      >
        {/* 상품 이미지 - showImage가 true일 때만 표시 */}
        {showImage && (
          <Box sx={{ width: photoSize, height: photoSize, borderRadius: 2, overflow: 'hidden', bgcolor: '#f5f5f5', mr: k * 0.01, flexShrink: 0 }}>
            <img
              src={image}
              alt={name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', userSelect: 'none' }}
              draggable={false}
            />
          </Box>
        )}
        {/* 상품 정보 */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', bgcolor: '#f9f9f9' }}>
          <Typography sx={{ fontWeight: 700, fontSize: nameFont, mb: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {brand}
          </Typography>
          <Typography sx={{ fontSize: descFont, color: '#222', mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: priceFont }}>{price.toLocaleString()}원</Typography>
        </Box>
      </Box>
    </Box>
  );
} 